// OperatorShell — dedicated Business Control Center shell (Blueprint §4.2–§4.6).
//
// Replaces the reuse of the firm `Shell`/`Sidebar` for operator routes (D-25).
// The sidebar IS the operator navigation (D-26): ten destinations, grouped,
// role-filtered. Renders an <Outlet/> for the /control/* route tree.
//
// Visual identity cue (Vision §15.2): a persistent deep-slate top band with the
// orange accent reserved for primary operator CTAs, so the Control Center reads
// as distinct from the firm's light workspace — using existing tokens only.

import { NavLink, Outlet } from "react-router-dom";
import type { ComponentType, ReactNode } from "react";
import {
  LayoutDashboard,
  ListChecks,
  Building2,
  Tag,
  TrendingUp,
  CreditCard,
  Coins,
  Activity,
  LifeBuoy,
  ShieldCheck,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";

export type PlatformRole =
  | "platform_owner"
  | "business_operator"
  | "billing_operator"
  | "support_operator"
  | "finance_reviewer"
  | "readonly_auditor";

interface NavItemDef {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  badge?: number;
  // Roles permitted to see this item. Omitted ⇒ all roles.
  roles?: PlatformRole[];
}

interface NavGroupDef {
  heading: string;
  items: NavItemDef[];
}

const ALL: PlatformRole[] = [
  "platform_owner",
  "business_operator",
  "billing_operator",
  "support_operator",
  "finance_reviewer",
  "readonly_auditor",
];

// IA from Blueprint §4.2. `roles` express the RBAC matrix (Vision §10 / S-1).
const NAV: NavGroupDef[] = [
  {
    heading: "Overview",
    items: [
      { to: "/control/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALL },
      { to: "/control/queue", label: "Work Queue", icon: ListChecks, roles: ["platform_owner", "business_operator", "billing_operator", "support_operator"] },
    ],
  },
  {
    heading: "Commercial",
    items: [
      { to: "/control/tenants", label: "Tenants", icon: Building2, roles: ALL },
      { to: "/control/offers", label: "Offers", icon: Tag, roles: ["platform_owner", "business_operator"] },
      { to: "/control/revenue", label: "Revenue", icon: TrendingUp, roles: ["platform_owner", "business_operator", "billing_operator", "finance_reviewer"] },
    ],
  },
  {
    heading: "Finance & Usage",
    items: [
      { to: "/control/billing", label: "Billing", icon: CreditCard, roles: ["platform_owner", "business_operator", "billing_operator", "finance_reviewer"] },
      { to: "/control/ai-credits", label: "AI Credits", icon: Coins, roles: ["platform_owner", "business_operator", "billing_operator"] },
      { to: "/control/platform-health", label: "Platform Health", icon: Activity, roles: ["platform_owner", "finance_reviewer", "billing_operator"] },
    ],
  },
  {
    heading: "Operations",
    items: [
      { to: "/control/support", label: "Support", icon: LifeBuoy, roles: ["platform_owner", "business_operator", "support_operator"] },
      { to: "/control/audit", label: "Audit", icon: ShieldCheck, roles: ALL },
    ],
  },
  {
    heading: "Config",
    items: [{ to: "/control/settings", label: "Settings", icon: SettingsIcon, roles: ["platform_owner"] }],
  },
];

function NavItem({ item }: { item: NavItemDef }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition ${
          isActive ? "bg-orange-500/10 text-orange-300" : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{item.label}</span>
      {typeof item.badge === "number" && item.badge > 0 ? (
        <span className="rounded-full bg-orange-500 px-1.5 text-[10px] font-black text-white">{item.badge}</span>
      ) : null}
    </NavLink>
  );
}

export interface OperatorIdentity {
  name: string;
  email?: string;
  platformRole: PlatformRole;
}

export function OperatorShell({
  operator,
  onSignOut,
  queueCount = 0,
  children,
}: {
  operator: OperatorIdentity;
  onSignOut?: () => void;
  queueCount?: number;
  children?: ReactNode;
}) {
  const groups = NAV.map((g) => ({
    ...g,
    items: g.items
      .filter((it) => !it.roles || it.roles.includes(operator.platformRole))
      .map((it) => (it.to === "/control/queue" ? { ...it, badge: queueCount } : it)),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex min-h-screen bg-[#0b0f17] text-slate-100">
      <aside className="flex w-64 shrink-0 flex-col border-r border-white/5 bg-[#0b0f17] px-3 py-4">
        <div className="px-2 pb-4">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-orange-400">i AM Vault</p>
          <p className="text-sm font-black tracking-tight text-white">Control Center</p>
        </div>
        <div className="mb-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
          <p className="truncate text-sm font-bold text-white">{operator.name}</p>
          <p className="text-[11px] font-semibold text-orange-300">{operator.platformRole.replace(/_/g, " ")}</p>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto">
          {groups.map((group) => (
            <div key={group.heading}>
              <p className="px-3 pb-1 text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">{group.heading}</p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavItem key={item.to} item={item} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        {onSignOut ? (
          <button
            type="button"
            onClick={onSignOut}
            className="mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-400 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        ) : null}
      </aside>
      <main className="min-w-0 flex-1 bg-[#f4f4f6] text-slate-900">
        <div className="mx-auto w-full max-w-7xl p-4 md:p-6">{children ?? <Outlet />}</div>
      </main>
    </div>
  );
}
